import { useEffect, useMemo } from 'react';
import { Col } from 'reactstrap';
import {
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import { useValidations } from '../../../../hooks/useValidations';
import MultipleFileInput from '../../../../components/fileInput/MultipleFileInput';
import PetitionFileDownload from './PetitionFileDownload';
import { PetitionData, ViewPetition } from '../../../../types/petitions';
import {
  deleteTempFile,
  uploadTempFile,
} from '../../../../api/requests/petitions';

interface PetitionFormCancelSemesterProps {
  careerOptions?: SelectOptionType[];
  downloadFiles?: ViewPetition['docs'];
  downloadFileResponse?: ViewPetition['docResponse'];
  onlyView?: boolean;
  isResolved?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  periodName?: string;
}

export default function PetitionFormCancelSemester({
  careerOptions = [],
  onlyView = false,
  isResolved = false,
  downloadFiles,
  downloadFileResponse,
  onLoadingChange,
  periodName,
}: PetitionFormCancelSemesterProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const { validateTextNotEmpty, validatePhone, validateEmail } =
    useValidations();
  const { control, setValue } = useFormContext<PetitionData>();

  const text = useMemo(() => {
    const prefix = 'modal.form';
    return {
      studyPlan: t(`${prefix}.studyPlan`),
      phone: t(`${prefix}.phone`),
      email: t(`${prefix}.email`),
      reason: {
        label: t(`${prefix}.reason.label`),
        placeholder: t(`${prefix}.reason.placeholder`),
      },
      file: {
        label: t(`${prefix}.file.label`),
        button: t(`${prefix}.file.button`),
      },
      download: {
        label: t(`${prefix}.download.label`),
        labelResponse: t(`${prefix}.download.labelResponse`),
        button: t(`${prefix}.download.button`),
      },
      validation: { select: t(`${prefix}.validation.select`) },
    };
  }, [t]);

  setValue('title', `Cancelación de ${periodName}`.trim());

  return (
    <>
      <Col md={6} className="my-3">
        <Select
          label={text.studyPlan}
          control={control}
          disabled={onlyView}
          options={careerOptions}
          rules={{ required: text.validation.select }}
          name="studyPlanEnrollment"
          isClearable={false}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label="Período"
          name="periodName"
          value={periodName}
          disabled
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.phone}
          name="cellPhone"
          disabled={onlyView}
          control={control}
          rules={{ validate: { validatePhone, require: validateTextNotEmpty } }}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.email}
          name="email"
          control={control}
          disabled={onlyView}
          rules={{ validate: { validateEmail, require: validateTextNotEmpty } }}
        />
      </Col>
      <Col md={12}>
        <TextAreaInput
          label={text.reason.label}
          name="description"
          placeholder={text.reason.placeholder}
          disabled={onlyView}
          rules={{
            validate: { require: validateTextNotEmpty },
            maxLength: { value: 500, message: 'Max 500' },
          }}
          control={control}
        />
      </Col>
      <Col md={12} className="my-4">
        {onlyView ? (
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.label}
            files={downloadFiles}
          />
        ) : (
          <MultipleFileInput
            name="docs"
            label={text.file.label}
            accept="image/png, image/jpeg, application/pdf"
            control={control}
            uploadTempFile={uploadTempFile}
            deleteTempFile={deleteTempFile}
            onLoadingChange={onLoadingChange}
          />
        )}
      </Col>
      {isResolved && downloadFileResponse && (
        <Col md={12} className="my-4">
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.labelResponse}
            file={downloadFileResponse}
          />
        </Col>
      )}
    </>
  );
}
